import React, { useCallback } from 'react';
import { useScrollTrigger } from '@mui/material';
import { PageAnchors } from './Header/PageAnchors';
import Header from './Header/Header';
import ErrorBoundary from '../ErrorBoundary';
import Breadcrumbs from '../Breadcrumbs';
import { ScrollProvider } from './ScrollContext';
import { HEADER_TRANSITION, HEADER_HEIGHT } from './Header/Header';
import classNames from 'classnames';
import { Theme, useMediaQuery } from '@mui/material';
import { useScrollController } from 'hooks/useScrollController';

interface PageLayoutProps {
  title: string;
  links: Array<{
    id: string;
    name: string;
    nameMobile: string;
    section: string;
    ref: React.RefObject<any>;
    ignoreOffset?: boolean;
  }>;
  sectionScrollRef: React.RefObject<any>;
  visibleSection: string | null;
  breadcrumbs?: {
    neighborhoodId: string;
    neighborhoodName: string;
    buildingName?: string;
    buildingId?: string;
    buildingPage?: boolean;
    preConstructionPage?: boolean;
    unitPage?: boolean;
    unitName?: string;
    unitUrl?: string;
    neighborhoodPage?: boolean;
  };
  scrollPastRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  headerContent: React.ReactNode;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  links,
  visibleSection,
  breadcrumbs,
  children,
  scrollPastRef,
  headerContent,
}) => {
  const { shouldShow } = useScrollController();
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isXl = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
      e.preventDefault();
      const sectionRef = links.find((link) => link.id === sectionId)?.ref;
      if (sectionRef?.current) {
        const currentScroll = window.pageYOffset;
        const targetScroll = sectionRef.current.offsetTop;

        // Only apply header offset when scrolling up
        const headerHeight =
          targetScroll < currentScroll
            ? isXl
              ? HEADER_HEIGHT.desktop
              : isTablet === false
              ? HEADER_HEIGHT.mobile
              : HEADER_HEIGHT.tablet
            : 0;

        // Add offset to account for spacing and hide previous section
        const offset = links.find((link) => link.id === sectionId)?.ignoreOffset
          ? 0
          : 50;

        window.scrollTo({
          top: targetScroll - headerHeight - offset,
          behavior: 'smooth',
        });

        window.history.pushState(null, '', `#${sectionId}`);
      }
    },
    [links, isXl, isTablet]
  );

  return (
    <ScrollProvider shouldShow={shouldShow}>
      <Header hideOnScroll={true} />
      {isDesktop && breadcrumbs && (
        <ErrorBoundary>
          <Breadcrumbs {...breadcrumbs} />
        </ErrorBoundary>
      )}
      {headerContent}
      <div
        className={classNames(
          'sticky z-100 bg-white ml-auto md:ml-0 flex md:flex-wrap mt-[17px] s:mt-[15px] md:mt-[15px]',
          "before:content-['']",
          'border-b',
          {
            'before:opacity-0': !shouldShow || !isDesktop,
          }
        )}
        style={{
          top: !shouldShow
            ? 0
            : `${
                isXl
                  ? HEADER_HEIGHT.desktop
                  : isTablet === false
                  ? HEADER_HEIGHT.mobile - 1
                  : HEADER_HEIGHT.tablet
              }px`,
          borderColor: shouldShow ? '#d2cfca' : 'transparent',
          transition: `top ${HEADER_TRANSITION.duration}ms ${HEADER_TRANSITION.easing}, border-color 300ms ease-out`,
        }}
      >
        <PageAnchors
          title={title}
          links={links}
          visibleSection={visibleSection}
          scrollPastRef={scrollPastRef}
          handleLinkClick={handleLinkClick}
          isHeaderHidden={shouldShow}
        />
      </div>
      {children}
    </ScrollProvider>
  );
};
